import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { Skeleton } from '../components/ui/skeleton'
import { GymsCarousel } from '../components/dashboard/GymsCarousel'
import { TrophiesSection } from '../components/dashboard/TrophiesSection'
import { StaffedSection } from '../components/dashboard/StaffedSection'
import { WeekWorkouts } from '../components/dashboard/WeekWorkouts'
import { TrainingPlanSection } from '../components/dashboard/TrainingPlanSection'
import { RecentSendsCarousel } from '../components/dashboard/RecentSendsCarousel'
import { LastMonthStats } from '../components/dashboard/LastMonthStats'
import { InstallAppCard } from '../components/dashboard/InstallAppCard'
import { PushNudgeCard } from '../components/push/PushNudgeCard'
import GuardianRequests from '../components/guardian/GuardianRequests'
import { dashboardService } from '../services/dashboard'
import type { DashboardData } from '../types/dashboard'

export default function DashboardPage() {
  const { t } = useTranslation()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  // Also called when a guardianship request is answered: opening a child's
  // account puts their circuits on this page, so it is re-read rather than
  // left stale until the next visit.
  const load = useCallback(() => {
    dashboardService
      .get()
      .then(setData)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // The dashboard is the landing page, so it leaves the navbar breadcrumb unset
  // and lets the DivePeaks logo stand.
  if (loading) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-6">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    )
  }

  if (failed || !data) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">{t('dashboard.loadError')}</p>
    )
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      {/* A child entered in a competition and this address named as their
          parent's: it stands at the top until it is answered, because it is
          the only thing on this page somebody else is waiting on. Renders
          nothing when there is nothing to answer. */}
      <GuardianRequests onAnswered={load} />
      {/* Turning notifications on, else installing the app: one offer at a
          time, nothing once neither is wanted or both were dismissed. */}
      <PushNudgeCard placement="dashboard" fallback={<InstallAppCard />} />
      <GymsCarousel gyms={data.gyms} />
      <TrophiesSection trophies={data.trophies ?? []} />
      <StaffedSection places={data.staffed ?? []} />
      <WeekWorkouts workouts={data.week_workouts} today={format(new Date(), 'yyyy-MM-dd')} />
      <TrainingPlanSection trainingPlan={data.training_plan} />
      <RecentSendsCarousel sends={data.recent_sends} />
      <LastMonthStats lastMonth={data.last_month} />
    </div>
  )
}
